import {
  DIGITAL_FORM_TAGS_APPOINTMENT_FORMS,
  DIGITAL_FORM_TAGS_EXCHANGE,
  DIGITAL_FORM_TAGS_LOSS_DAMAGE,
  DIGITAL_FORM_TAGS_ORDER,
  DIGITAL_FORM_TAGS_REPAIR
} from "constants/feature-flags";
import {
  APPOINTMENT_FORM_TEMPLATE_TYPE,
  EXCHANGE_FORM_TEMPLATE_TYPE,
  LOANER_FORM_TEMPLATE_TYPE,
  LOSS_DAMAGE_FORM_TEMPLATE_TYPE,
  ORDER_FORM_TEMPLATE_TYPE,
  PATIENT_FORM_TEMPLATE_TYPE,
  REPAIR_FORM_TEMPLATE_TYPE
} from "constants/form-templates";

import { APPOINTMENT_PROVIDER_MENU_OPTIONS } from "./appointment-provider.utils";
import { PATIENT_NAME_OPTIONS } from "./patient-info/patient-name.utils";
import type { MergeTagMenuEntry } from "./signature-merge-tags.utils";

/*
 * Form-context merge tags (ENG-2277, tech plan D7). Their values exist only while a
 * form is filled from one of the hearing-aid request dialogs: the repair's devices,
 * the order's date, the loaner's due date. They are deliberately NOT part of
 * MERGE_TAGS_OPTIONS — a form template is offered only the groups its ticked
 * placements can fill (`groupsFor`).
 *
 * Every device group carries two layouts, because paper forms come in both:
 * - Left / Right, filled by each device's ear;
 * - Device1 / Device2, filled by position, as the Anvil payloads are.
 * A device with no ear fills only its positional slot.
 *
 * The tags are generated from the specs below, e.g. {{Request.Left.SerialNumber}},
 * {{Request.Device1.Warranty}}, {{Order.Device2.Color}}, {{Loaner.DueDate}},
 * {{LossDamage.ClaimReason}}. Each leaf's `path` is where its value sits in the
 * editor's `additionalMergeTagsValues` (request.left.serialNumber, …).
 */

export type FormContextLeaf = {
  /** The tag without braces, e.g. "Request.Left.SerialNumber". */
  value: string;
  /** The picker label, e.g. "Request - Left - Serial Number". */
  title: string;
  /** Where the value sits in `additionalMergeTagsValues`, e.g. ["request", "left", "serialNumber"]. */
  path: readonly string[];
};

type Segment = { tag: string; key: string; label: string };

type GroupSpec = Segment & {
  menuTitle: string;
  fields: Segment[];
  deviceFields: Segment[];
  /**
   * Fields offered only where the surface's own flag is on (ENG-2493 onward): the group's extended menu
   * adds them after its existing fields, and its base menu stays exactly as it was.
   */
  newFields?: Segment[];
  newDeviceFields?: Segment[];
  /**
   * Tags offered first in the extended menu that read something other than the group's context (e.g. the
   * patient), so they fill on every surface: they are menu items only, never the group's leaves.
   */
  extendedLeadItems?: MergeTagMenuEntry[];
};

const NEW_SUFFIX = " · New";

export const FORM_CONTEXT_DEVICE_SLOTS: readonly Segment[] = [
  { tag: "Left", key: "left", label: "Left" },
  { tag: "Right", key: "right", label: "Right" },
  { tag: "Device1", key: "device1", label: "Device 1" },
  { tag: "Device2", key: "device2", label: "Device 2" }
];

const EAR = { tag: "Ear", key: "ear", label: "Ear" };
const NAME = { tag: "Name", key: "name", label: "Name" };
const MANUFACTURER = {
  tag: "Manufacturer",
  key: "manufacturer",
  label: "Manufacturer"
};
const MODEL = { tag: "Model", key: "model", label: "Model" };
const SERIAL_NUMBER = {
  tag: "SerialNumber",
  key: "serialNumber",
  label: "Serial Number"
};
const DATE = { tag: "Date", key: "date", label: "Date" };
const LOCATION = { tag: "Location", key: "location", label: "Location" };

// Fields the order and repair forms both gained (ENG-2493, ENG-2494).
const BILLING_ACCOUNT_NUMBER = {
  tag: "BillingAccountNumber",
  key: "billingAccountNumber",
  label: "Billing Account Number"
};
const SHIPPING_ACCOUNT_NUMBER = {
  tag: "ShippingAccountNumber",
  key: "shippingAccountNumber",
  label: "Shipping Account Number"
};
const SHIPPING_METHOD = {
  tag: "ShippingMethod",
  key: "shippingMethod",
  label: "Shipping Method"
};
const NOTES = { tag: "Notes", key: "notes", label: "Notes" };
const CONTACT_FIELDS = [
  {
    tag: "ContactFirstName",
    key: "contactFirstName",
    label: "Contact First Name"
  },
  {
    tag: "ContactLastName",
    key: "contactLastName",
    label: "Contact Last Name"
  },
  {
    tag: "ContactFullName",
    key: "contactFullName",
    label: "Contact Full Name"
  },
  { tag: "ContactEmail", key: "contactEmail", label: "Contact Email" }
];
const CLINIC_ORDER_NUMBER = {
  tag: "ClinicOrderNumber",
  key: "clinicOrderNumber",
  label: "Clinic Order Number"
};
const SUPPLIER_ORDER_NUMBER = {
  tag: "SupplierOrderNumber",
  key: "supplierOrderNumber",
  label: "Supplier Order Number"
};
const VENDOR_FIELDS = [
  { tag: "VendorName", key: "vendorName", label: "Vendor Name" },
  { tag: "VendorPhone", key: "vendorPhone", label: "Vendor Phone" }
];
const PURCHASE_DATE = {
  tag: "PurchaseDate",
  key: "purchaseDate",
  label: "Purchase Date"
};

const REQUEST_SPEC: GroupSpec = {
  tag: "Request",
  key: "request",
  label: "Request",
  menuTitle: "Repair / Exchange / Loss & Damage",
  fields: [DATE, LOCATION],
  deviceFields: [
    EAR,
    NAME,
    MANUFACTURER,
    MODEL,
    SERIAL_NUMBER,
    { tag: "Warranty", key: "warranty", label: "Warranty" },
    // The manufacturer warranty's last day; Warranty above is its type (in or out of warranty).
    {
      tag: "WarrantyExpires",
      key: "warrantyExpires",
      label: "Warranty expires"
    }
  ],
  // ENG-2494: what the Anvil repair form also prints, the order behind the dialog and the repaired devices' own
  // facts (flags `digital-form-tags-repair`, `-exchange`, `-loss-damage`, one per surface).
  newFields: [
    ...CONTACT_FIELDS,
    NOTES,
    SHIPPING_METHOD,
    BILLING_ACCOUNT_NUMBER,
    SHIPPING_ACCOUNT_NUMBER,
    { tag: "OrderId", key: "orderId", label: "Order ID" },
    CLINIC_ORDER_NUMBER,
    SUPPLIER_ORDER_NUMBER,
    {
      tag: "OrderCreatedDate",
      key: "orderCreatedDate",
      label: "Order Created Date"
    },
    { tag: "OrderStatus", key: "orderStatus", label: "Order Status" },
    ...VENDOR_FIELDS
  ],
  newDeviceFields: [
    { tag: "RepairReasons", key: "repairReasons", label: "Repair Reasons" },
    { tag: "Modifications", key: "modifications", label: "Modifications" },
    PURCHASE_DATE,
    {
      tag: "LossDamageWarrantyExpires",
      key: "lossDamageWarrantyExpires",
      label: "L&D Warranty Expires"
    },
    {
      tag: "ExtendedWarrantyExpires",
      key: "extendedWarrantyExpires",
      label: "Extended Warranty Expires"
    },
    { tag: "Color", key: "color", label: "Color" }
  ]
};

const LOSS_DAMAGE_SPEC: GroupSpec = {
  tag: "LossDamage",
  key: "lossDamage",
  label: "Loss & Damage",
  menuTitle: "Loss & Damage claim",
  fields: [{ tag: "ClaimReason", key: "claimReason", label: "Claim Reason" }],
  deviceFields: [],
  // ENG-2496: the claim date and each claimed device's own L&D facts (flag `digital-form-tags-loss-damage`). The
  // device's purchase date and L&D warranty are the Request group's, so they are not repeated here.
  newFields: [{ tag: "ClaimDate", key: "claimDate", label: "Claim Date" }],
  newDeviceFields: [
    { tag: "ClaimReason", key: "claimReason", label: "Claim Reason" },
    { tag: "Service", key: "service", label: "L&D Service" },
    { tag: "ServicePrice", key: "servicePrice", label: "L&D Service Price" },
    {
      tag: "DeductibleOnRecord",
      key: "deductibleOnRecord",
      label: "L&D Deductible on Record"
    }
  ]
};

const ORDER_SPEC: GroupSpec = {
  tag: "Order",
  key: "order",
  label: "Order",
  menuTitle: "Order",
  fields: [DATE, LOCATION],
  deviceFields: [
    EAR,
    NAME,
    MANUFACTURER,
    MODEL,
    SERIAL_NUMBER,
    { tag: "Color", key: "color", label: "Color" },
    { tag: "Battery", key: "battery", label: "Battery" }
  ],
  // ENG-2493 (D10): the Anvil order form prints the full middle name; the picker otherwise has the initial.
  extendedLeadItems: [
    { title: "Patient - Middle Name", value: PATIENT_NAME_OPTIONS.MiddleName }
  ],
  // ENG-2493: what the Anvil order form also prints, and the order record's own numbers (flag
  // `digital-form-tags-order`).
  newFields: [
    BILLING_ACCOUNT_NUMBER,
    SHIPPING_ACCOUNT_NUMBER,
    SHIPPING_METHOD,
    NOTES,
    ...CONTACT_FIELDS,
    CLINIC_ORDER_NUMBER,
    SUPPLIER_ORDER_NUMBER,
    { tag: "CreatedDate", key: "createdDate", label: "Created Date" },
    { tag: "Status", key: "status", label: "Status" },
    ...VENDOR_FIELDS
  ],
  newDeviceFields: [
    { tag: "Condition", key: "condition", label: "Condition" },
    PURCHASE_DATE
  ]
};

const LOANER_SPEC: GroupSpec = {
  tag: "Loaner",
  key: "loaner",
  label: "Loaner",
  menuTitle: "Loaner / Demo",
  fields: [
    { tag: "LoanDate", key: "loanDate", label: "Loan Date" },
    { tag: "DueDate", key: "dueDate", label: "Due Date" }
  ],
  deviceFields: [MANUFACTURER, MODEL, SERIAL_NUMBER]
};

// ENG-2495: the device that replaced each one going back, beside it in the same slot. Every field is new, so the
// group has no base menu and is offered only where `digital-form-tags-exchange` is on.
const EXCHANGE_SPEC: GroupSpec = {
  tag: "Exchange",
  key: "exchange",
  label: "Exchange",
  menuTitle: "Exchange (replacement)",
  fields: [],
  deviceFields: [],
  newFields: [
    { tag: "ExchangedDate", key: "exchangedDate", label: "Exchanged Date" },
    {
      tag: "ReturnedToManufacturer",
      key: "returnedToManufacturer",
      label: "Returned to Manufacturer"
    }
  ],
  newDeviceFields: [EAR, NAME, MANUFACTURER, MODEL, SERIAL_NUMBER].map(
    (field) => ({ ...field, label: `Replacement ${field.label}` })
  )
};

// ENG-2499: the appointment a form is printed for (`appointmentFormValues`), under
// `digital-form-tags-appointment-forms`. All new fields, so no base menu. The provider's name and signature keep their
// `boundAppointment` tags, which already read this appointment, so they lead the menu and aren't leaves here.
const APPOINTMENT_SPEC: GroupSpec = {
  tag: "Appointment",
  key: "appointment",
  label: "Appointment",
  menuTitle: "Appointment",
  fields: [],
  deviceFields: [],
  extendedLeadItems: APPOINTMENT_PROVIDER_MENU_OPTIONS.menu,
  newFields: [
    { tag: "Type", key: "type", label: "Type" },
    { tag: "Status", key: "status", label: "Status" },
    { tag: "Date", key: "date", label: "Date" },
    { tag: "Time", key: "time", label: "Time" },
    { tag: "TimeRange", key: "timeRange", label: "Start - End Time" },
    { tag: "Duration", key: "duration", label: "Duration" },
    { tag: "Room", key: "room", label: "Location / Room" },
    { tag: "Clinic", key: "clinic", label: "Clinic" },
    { tag: "Notes", key: "notes", label: "Notes" },
    { tag: "Companion", key: "companion", label: "Companion" },
    { tag: "TelehealthLink", key: "telehealthLink", label: "Telehealth Link" },
    { tag: "ReferralSource", key: "referralSource", label: "Referral Source" },
    {
      tag: "Provider.LicenseNumber",
      key: "providerLicenseNumber",
      label: "Provider License Number"
    },
    { tag: "Provider.NPI", key: "providerNpi", label: "Provider NPI" },
    {
      tag: "DirectSupervisor.Name",
      key: "directSupervisorName",
      label: "Direct Supervising Provider Name"
    },
    {
      tag: "DirectSupervisor.Signature",
      key: "directSupervisorSignature",
      label: "Direct Supervising Provider Signature"
    },
    {
      tag: "Supervisor.Name",
      key: "supervisorName",
      label: "Supervising Provider Name"
    },
    {
      tag: "Supervisor.Signature",
      key: "supervisorSignature",
      label: "Supervising Provider Signature"
    }
  ]
};

const toLeaf = (segments: Segment[]): FormContextLeaf => ({
  value: segments.map((segment) => segment.tag).join("."),
  title: segments.map((segment) => segment.label).join(" - "),
  path: segments.map((segment) => segment.key)
});

const toMenuItem = ({ title, value }: FormContextLeaf): MergeTagMenuEntry => ({
  title,
  value
});

const buildMenu = (
  spec: GroupSpec,
  fields: Segment[],
  deviceFields: Segment[],
  deviceSlots: readonly Segment[] = FORM_CONTEXT_DEVICE_SLOTS
) => {
  const topLeaves = fields.map((field) => toLeaf([spec, field]));
  const slots = deviceFields.length
    ? deviceSlots.map((slot) => ({
        slot,
        leaves: deviceFields.map((field) => toLeaf([spec, slot, field]))
      }))
    : [];

  const menu: MergeTagMenuEntry = {
    title: spec.menuTitle + NEW_SUFFIX,
    menu: [
      ...topLeaves.map(toMenuItem),
      ...slots.map(({ slot, leaves }) => ({
        title: slot.label,
        menu: leaves.map(toMenuItem)
      }))
    ]
  };

  return {
    menu,
    leaves: [...topLeaves, ...slots.flatMap(({ leaves }) => leaves)]
  };
};

// The base menu is the group as it was before any surface flag; the extended one adds the flagged fields.
// `leaves` holds both, so every tag has a value handler and a title wherever a form already holds it.
const buildGroup = (
  spec: GroupSpec,
  deviceSlots: readonly Segment[] = FORM_CONTEXT_DEVICE_SLOTS
) => {
  const base = buildMenu(spec, spec.fields, spec.deviceFields, deviceSlots);
  if (!spec.newFields?.length && !spec.newDeviceFields?.length) {
    return { ...base, extendedMenu: base.menu };
  }
  const extended = buildMenu(
    spec,
    [...spec.fields, ...(spec.newFields ?? [])],
    [...spec.deviceFields, ...(spec.newDeviceFields ?? [])],
    deviceSlots
  );
  const baseValues = new Set(base.leaves.map(({ value }) => value));
  return {
    menu: base.menu,
    extendedMenu: {
      ...extended.menu,
      menu: [...(spec.extendedLeadItems ?? []), ...(extended.menu.menu ?? [])]
    },
    leaves: [
      ...base.leaves,
      ...extended.leaves.filter(({ value }) => !baseValues.has(value))
    ]
  };
};

const REQUEST_GROUP = buildGroup(REQUEST_SPEC);
const LOSS_DAMAGE_GROUP = buildGroup(LOSS_DAMAGE_SPEC);
const ORDER_GROUP = buildGroup(ORDER_SPEC);
const LOANER_GROUP = buildGroup(LOANER_SPEC);
const EXCHANGE_GROUP = buildGroup(EXCHANGE_SPEC);
const APPOINTMENT_GROUP = buildGroup(APPOINTMENT_SPEC);

// ENG-2495: on a form that is only an exchange form, the Request devices are the ones going back to the
// manufacturer, so their slots say so. Same tags, values and paths; only the picker's slot labels differ.
const REQUEST_GOING_BACK_MENU = buildGroup(
  REQUEST_SPEC,
  FORM_CONTEXT_DEVICE_SLOTS.map((slot) => ({
    ...slot,
    label: `${slot.label} (going back)`
  }))
).extendedMenu;

export const REQUEST_MENU_OPTIONS = REQUEST_GROUP.menu;
export const LOSS_DAMAGE_MENU_OPTIONS = LOSS_DAMAGE_GROUP.menu;
export const ORDER_MENU_OPTIONS = ORDER_GROUP.menu;
export const LOANER_MENU_OPTIONS = LOANER_GROUP.menu;
const EXCHANGE_MENU_OPTIONS = EXCHANGE_GROUP.menu;
const APPOINTMENT_MENU_OPTIONS = APPOINTMENT_GROUP.menu;

// Also the order `groupsFor` returns them in.
const GROUPS = [
  REQUEST_GROUP,
  LOSS_DAMAGE_GROUP,
  ORDER_GROUP,
  LOANER_GROUP,
  EXCHANGE_GROUP,
  APPOINTMENT_GROUP
];

/** Each group's menu as a flagged surface offers it, for naming the tags only those menus hold. */
export const FORM_CONTEXT_EXTENDED_MENUS: readonly MergeTagMenuEntry[] =
  GROUPS.map((group) => group.extendedMenu);

export const FORM_CONTEXT_LEAVES: readonly FormContextLeaf[] = GROUPS.flatMap(
  (group) => group.leaves
);

export const FORM_CONTEXT_TAG_VALUES = new Set<string>(
  FORM_CONTEXT_LEAVES.map((leaf) => leaf.value)
);

/**
 * Which context groups a form placement (FormTemplateType) can fill. Patient forms get the
 * base catalog only.
 */
export const PLACEMENT_MERGE_TAG_GROUPS: Record<number, MergeTagMenuEntry[]> = {
  [REPAIR_FORM_TEMPLATE_TYPE]: [REQUEST_MENU_OPTIONS],
  [EXCHANGE_FORM_TEMPLATE_TYPE]: [REQUEST_MENU_OPTIONS, EXCHANGE_MENU_OPTIONS],
  [LOSS_DAMAGE_FORM_TEMPLATE_TYPE]: [
    REQUEST_MENU_OPTIONS,
    LOSS_DAMAGE_MENU_OPTIONS
  ],
  [ORDER_FORM_TEMPLATE_TYPE]: [ORDER_MENU_OPTIONS],
  [LOANER_FORM_TEMPLATE_TYPE]: [LOANER_MENU_OPTIONS],
  [PATIENT_FORM_TEMPLATE_TYPE]: [],
  [APPOINTMENT_FORM_TEMPLATE_TYPE]: [APPOINTMENT_MENU_OPTIONS]
};

// Each surface's own flag (ENG-2493 onward): with it on, that placement is offered its groups' extended menus.
const PLACEMENT_TAG_FLAGS: Record<number, string> = {
  [ORDER_FORM_TEMPLATE_TYPE]: DIGITAL_FORM_TAGS_ORDER,
  [REPAIR_FORM_TEMPLATE_TYPE]: DIGITAL_FORM_TAGS_REPAIR,
  [EXCHANGE_FORM_TEMPLATE_TYPE]: DIGITAL_FORM_TAGS_EXCHANGE,
  [LOSS_DAMAGE_FORM_TEMPLATE_TYPE]: DIGITAL_FORM_TAGS_LOSS_DAMAGE,
  [APPOINTMENT_FORM_TEMPLATE_TYPE]: DIGITAL_FORM_TAGS_APPOINTMENT_FORMS
};

/** The placements whose surface flag is on, i.e. those offered their groups' extended menus. */
export const contextTagPlacements = (
  flags: Record<string, unknown>
): number[] =>
  Object.entries(PLACEMENT_TAG_FLAGS)
    .filter(([, flag]) => Boolean(flags[flag]))
    .map(([placement]) => Number(placement));

const EXTENDED_MENU = new Map(
  GROUPS.map((group) => [group.menu, group.extendedMenu])
);

/**
 * The context groups to offer a template ticked for `placements`: deduped, in catalog order. A group is offered
 * extended when a placement in `tagPlacements` offers it (a superset of the base menu, so one menu per group).
 * A group whose base menu is empty (every field is new, as Exchange's) is offered only extended.
 */
export const groupsFor = (
  placements: number[],
  tagPlacements: number[] = []
): MergeTagMenuEntry[] => {
  const menusOf = (list: number[]) =>
    new Set(
      list.flatMap((placement) => PLACEMENT_MERGE_TAG_GROUPS[placement] ?? [])
    );
  const offered = menusOf(placements);
  const flagged = new Set(tagPlacements);
  const extended = menusOf(
    placements.filter((placement) => flagged.has(placement))
  );
  const requestOnlyOnExchange = placements
    .filter((placement) =>
      PLACEMENT_MERGE_TAG_GROUPS[placement]?.includes(REQUEST_MENU_OPTIONS)
    )
    .every((placement) => placement === EXCHANGE_FORM_TEMPLATE_TYPE);

  const offeredMenu = (menu: MergeTagMenuEntry) => {
    if (!extended.has(menu)) return menu;
    if (menu === REQUEST_MENU_OPTIONS && requestOnlyOnExchange) {
      return REQUEST_GOING_BACK_MENU;
    }
    return EXTENDED_MENU.get(menu) ?? menu;
  };
  return GROUPS.map((group) => group.menu)
    .filter((menu) => offered.has(menu))
    .filter((menu) => menu.menu?.length || extended.has(menu))
    .map(offeredMenu);
};

// Accepts "Name", "{{Name}}" or "{{Name(options)}}".
const bareTagName = (tag: string) =>
  tag.replace(/^\{\{/, "").replace(/\}\}$/, "").split("(")[0].trim();

const GROUP_VALUES = new Map(
  GROUPS.map((group) => [
    group.menu,
    new Set(group.leaves.map((leaf) => leaf.value))
  ])
);

/**
 * The ticked placements on which a context tag would print blank, for the PDF
 * check's "won't fill on <surface>". A base-catalog tag is not a context tag and
 * returns nothing. Flag-agnostic: it reads every group a placement can fill, offered or not.
 */
export const surfacesMissingTag = (
  tag: string,
  placements: number[]
): number[] => {
  const name = bareTagName(tag);
  if (!FORM_CONTEXT_TAG_VALUES.has(name)) return [];

  return Array.from(new Set(placements)).filter(
    (placement) =>
      !(PLACEMENT_MERGE_TAG_GROUPS[placement] ?? []).some((menu) =>
        GROUP_VALUES.get(menu)?.has(name)
      )
  );
};
